import { PopoverFilter } from '@application/components/filters';
import { UserContext } from '@application/contexts';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, TrackChangesOutlined } from '@mui/icons-material';
import { Box, ButtonBase, Slider } from '@mui/material';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { siteLookupTokens } from '../siteLookupTokens';

const defaultRadius = 5;

type Props = {
  /** a compact "within N mi" trigger, for use inside the address field */
  embedded?: boolean;
  onChange?: (value: number) => void;
  readonly?: boolean;
  value: number;
};

const RadiusFilter = ({ embedded, onChange, readonly, value }: Props) => {
  const { t } = useTranslation();

  const valueLabelFormat = (value: number) => t('suffix.unit.mile', { count: value });

  const { tenantPermissions } = useContext(UserContext);

  return (
    <PopoverFilter
      icon={<TrackChangesOutlined />}
      name={t('siteLookup.filters.radius')}
      onReset={() => onChange?.(defaultRadius)}
      readonly={readonly}
      valueLabel={t('suffix.unit.mileUpTo', { count: value })}
      renderTrigger={
        embedded
          ? ({ controlsId, onClick, open }) => (
              <ButtonBase
                aria-controls={open ? controlsId : undefined}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label={`${t('siteLookup.filters.radius')}: ${t('siteLookup.radiusWithin', { count: value })}`}
                disabled={readonly}
                onClick={onClick}
                sx={{
                  borderRadius: '8px',
                  color: siteLookupTokens.color.text.secondary,
                  fontSize: 14,
                  fontWeight: 500,
                  gap: 0.25,
                  height: '100%',
                  paddingLeft: 1.5,
                  paddingRight: 1,
                  whiteSpace: 'nowrap',
                  '&:hover': { color: siteLookupTokens.color.text.primary },
                  '&.Mui-focusVisible': { outline: `2px solid ${siteLookupTokens.color.primary}`, outlineOffset: -2 },
                }}
              >
                {t('siteLookup.radiusWithin', { count: value })}
                {open ? <KeyboardArrowUpOutlined fontSize="small" aria-hidden /> : <KeyboardArrowDownOutlined fontSize="small" aria-hidden />}
              </ButtonBase>
            )
          : undefined
      }
    >
      <Box minWidth={460} paddingLeft={2} paddingRight={3}>
        <Slider
          onChange={(_, value) => onChange?.(value as number)}
          value={value}
          step={1}
          min={1}
          max={tenantPermissions?.maxRange}
          marks={[
            { value: 1, label: 1 },
            { value: 5, label: 5 },
            { value: 10, label: 10 },
            { value: 15, label: 15 },
            { value: 20, label: 20 },
          ]}
          valueLabelDisplay="auto"
          valueLabelFormat={valueLabelFormat}
          sx={{
            '& .MuiSlider-valueLabel': { backgroundColor: 'primary.main' },
            '& .MuiSlider-mark': { borderRadius: 100, height: 6, width: 6 },
            '& .MuiSlider-markLabel': {
              typography: 'body1',
              ':not(.MuiSlider-markLabel ~ .MuiSlider-markLabel)': { paddingLeft: 1 },
              ':not(:has(~ .MuiSlider-markLabel))': { paddingRight: 1 },
            },
          }}
        />
      </Box>
    </PopoverFilter>
  );
};

export default RadiusFilter;
